/**
 * Open-advance entries section of the Wallet tab (ENG-27316, refund action
 * ENG-27799).
 *
 * Since ENG-27743 the ledger no longer shows held credit being parked or set
 * off, so this is the one place a vendor sees it: a table of the factory's
 * entries, filtered by state, each expandable to the invoices it was set off
 * against. The only action is recording a refund UTR on an expired entry —
 * set-offs and expiry are automatic.
 *
 * Open and Expired come from the tab's active-entries query (always small).
 * Refunded and Used up grow forever, so they are fetched only when chosen, a
 * page at a time; All is the active entries followed by that history.
 *
 * Self-contained so its placement (below the facility table today; Product may
 * move it behind a tab) is a one-line change in Wallet.tsx.
 */
import { Fragment, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

import type { WalletAdjustment, WalletEntry } from "../types/wallet";
import { WALLET_ENTRY_STATE } from "../types/wallet";
import { daysUntil, ENTRY_GROUPS, entrySourceLabel } from "../utils/openAdvance";
import { HISTORY_ENTRY_STATES, useWalletEntryHistory } from "../api/walletQueries";
import { formatEpochDate } from "../utils/walletLedger";
import { formatAmount } from "../utils/formatters";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import { useIsMobile } from "@/hooks/useIsMobile";
import MobileCard from "@/components/mobile/MobileCard";
import FieldRow from "@/components/mobile/FieldRow";

interface OpenAdvanceEntriesProps {
  /** Open + Expired entries — the tab already holds these for its cards. */
  entries: WalletEntry[];
  /** For fetching refunded / used-up history on demand. */
  factoryCode: string;
  /** The Wallet tab's facility names, preferred over the entry's best-effort one. */
  facilityNames: Map<number, string>;
  today: Date;
  canRecordRefund: boolean;
  onRecordRefund: (entry: WalletEntry) => void;
}

type Filter = "ALL" | (typeof ENTRY_GROUPS)[number]["state"];

const STATE_CHIP: Record<string, { label: string; color: string; bg: string }> = {
  [WALLET_ENTRY_STATE.OPEN]: { label: "Open", color: V2_COLORS.primary, bg: V2_COLORS.primaryBg },
  [WALLET_ENTRY_STATE.EXPIRED]: { label: "Expired", color: V2_COLORS.error, bg: V2_COLORS.errorBg },
  [WALLET_ENTRY_STATE.REFUNDED]: { label: "Refunded", color: V2_COLORS.successSoft, bg: V2_COLORS.successSoftBg },
  [WALLET_ENTRY_STATE.CONSUMED]: { label: "Used up", color: V2_COLORS.textSecondary, bg: V2_COLORS.bgMuted },
};

/** "DD Mon YYYY" for an ISO stamp, "—" when absent. */
function formatIsoDate(iso: string | null | undefined): string {
  return iso ? formatEpochDate(new Date(iso).getTime()) : "—";
}

function StateChip({ state }: { state: string }) {
  const meta = STATE_CHIP[state] ?? { label: state, color: V2_COLORS.textSecondary, bg: V2_COLORS.bgMuted };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{ color: meta.color, bgcolor: meta.bg, fontWeight: 600, fontSize: "11px", height: 22 }}
    />
  );
}

/** What payments have drawn from the entry: `amount` never changes, `balance` is what is left. */
function usedAmount(entry: WalletEntry): number {
  return Math.max(0, entry.amount - entry.balance);
}

/** The expiry column: when it expires, that it expired, or how it was settled. */
function ExpiryCell({ entry, today }: { entry: WalletEntry; today: Date }) {
  let main = "—";
  let sub = "";
  let hint = "";
  let color: string | undefined;
  if (entry.state === WALLET_ENTRY_STATE.OPEN) {
    const left = daysUntil(entry.expires_at, today);
    main = formatIsoDate(entry.expires_at);
    sub = left === 0 ? "Expires today" : `${left} ${left === 1 ? "day" : "days"} left`;
    color = left <= 10 ? V2_COLORS.warning : undefined;
  } else if (entry.state === WALLET_ENTRY_STATE.EXPIRED) {
    main = formatIsoDate(entry.expired_at ?? entry.expires_at);
    sub = "Refund due";
    // No hold length here: the stamp already says when it expired. A partly
    // used entry must not claim nothing used it (ENG-28746).
    // VIP only expires an entry with balance > 0, but never ask for a ₹0.00 transfer.
    const used = usedAmount(entry);
    if (entry.balance > 0) {
      hint =
        used > 0
          ? `${formatAmount(used)} of this credit was used. Transfer the remaining ${formatAmount(entry.balance)} back in one payment and record the UTR.`
          : `No payment used this credit before it expired. Transfer ${formatAmount(entry.balance)} back in one payment and record the UTR.`;
    }
    color = V2_COLORS.error;
  } else if (entry.state === WALLET_ENTRY_STATE.REFUNDED) {
    main = `Refunded ${formatIsoDate(entry.refunded_at)}`;
    sub = entry.refund_utr ? `UTR ${entry.refund_utr}` : "";
  }
  return (
    <>
      <Typography fontSize="13px">{main}</Typography>
      {sub && (
        <Typography fontSize="12px" color={color ?? "text.secondary"}>
          {sub}
        </Typography>
      )}
      {hint && (
        <Typography fontSize="11px" color="text.secondary" mt={0.25} sx={{ maxWidth: 260 }}>
          {hint}
        </Typography>
      )}
    </>
  );
}

/** Balance with what has been used, or what came back for a refunded entry. */
function BalanceCell({ entry }: { entry: WalletEntry }) {
  const used = usedAmount(entry);
  let sub = "";
  if (entry.state === WALLET_ENTRY_STATE.REFUNDED) {
    sub = entry.refunded_amount != null ? `${formatAmount(entry.refunded_amount)} refunded` : "Refunded";
  } else if (used > 0) {
    sub = `${formatAmount(used)} used`;
  }
  return (
    <>
      <Typography fontSize="13px" fontWeight={600}>
        {formatAmount(entry.balance)}
      </Typography>
      {sub && (
        <Typography fontSize="12px" color="text.secondary">
          {sub}
        </Typography>
      )}
    </>
  );
}

/** One set-off line. A released line was handed back, so it reads as returned. */
function SetOffLine({ line }: { line: WalletAdjustment }) {
  const released = !!line.released_at;
  const invoice = line.invoice_number ? `Invoice ${line.invoice_number}` : "An invoice";
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      gap={2}
      sx={{ textDecoration: released ? "line-through" : "none", color: released ? "text.secondary" : "inherit" }}
    >
      <Typography fontSize="12px" color="inherit">
        {invoice} · {formatIsoDate(line.created_at)}
        {released && " · returned"}
      </Typography>
      <Typography fontSize="12px" fontWeight={600} color="inherit" sx={{ whiteSpace: "nowrap" }}>
        −{formatAmount(line.amount)}
      </Typography>
    </Box>
  );
}

function SetOffList({ lines }: { lines: WalletAdjustment[] }) {
  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography fontSize="11px" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Set off against
      </Typography>
      {lines.map((line) => (
        <SetOffLine key={line.id} line={line} />
      ))}
    </Box>
  );
}

function RefundButton({ entry, onRecordRefund }: { entry: WalletEntry; onRecordRefund: (e: WalletEntry) => void }) {
  return (
    <Button
      size="small"
      variant="contained"
      onClick={() => onRecordRefund(entry)}
      sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", whiteSpace: "nowrap" }}
    >
      Record refund
    </Button>
  );
}

export default function OpenAdvanceEntries({
  entries,
  factoryCode,
  facilityNames,
  today,
  canRecordRefund,
  onRecordRefund,
}: OpenAdvanceEntriesProps) {
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState<Filter>(WALLET_ENTRY_STATE.OPEN);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // History is fetched only for the chips that need it.
  const historyStates =
    filter === "ALL"
      ? HISTORY_ENTRY_STATES
      : filter === WALLET_ENTRY_STATE.REFUNDED || filter === WALLET_ENTRY_STATE.CONSUMED
        ? [filter]
        : [];
  const history = useWalletEntryHistory(factoryCode, historyStates, historyStates.length > 0);
  // Offset paging can repeat a row when an entry settles between pages; keep the first.
  const seen = new Set<number>();
  const historyEntries = (history.data?.pages ?? [])
    .flatMap((page) => page.entries)
    .filter((e) => !seen.has(e.id) && !!seen.add(e.id));

  const visible =
    filter === "ALL"
      ? [...entries, ...historyEntries]
      : historyStates.length > 0
        ? historyEntries
        : entries.filter((e) => e.state === filter);

  // Only worth a column when the rows actually span facilities.
  const showFacility = new Set(visible.map((e) => e.vendor_facility_id)).size > 1;
  const facilityName = (entry: WalletEntry) =>
    facilityNames.get(entry.vendor_facility_id) ||
    entry.vendor_facility_name ||
    `Facility #${entry.vendor_facility_id}`;

  // Counts only where the data is complete: history has no total.
  const activeCount = (state: string) => entries.filter((e) => e.state === state).length;
  const filters: { key: Filter; label: string }[] = [
    ...ENTRY_GROUPS.map((g) => ({
      key: g.state,
      label:
        g.state === WALLET_ENTRY_STATE.OPEN || g.state === WALLET_ENTRY_STATE.EXPIRED
          ? `${g.label} (${activeCount(g.state)})`
          : g.label,
    })),
    { key: "ALL", label: "All" },
  ];
  const historyLoading = historyStates.length > 0 && history.isInitialLoading;

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const canRefund = (entry: WalletEntry) => canRecordRefund && entry.state === WALLET_ENTRY_STATE.EXPIRED;
  const columns = [...(showFacility ? ["Facility"] : []), "Source", "Credit", "Balance", "Expires", "State", ""];

  return (
    <Box mt={3}>
      <Typography fontWeight={700} fontSize="16px">
        Open advance
      </Typography>
      <Typography fontSize="12px" color="text.secondary" mt={0.5}>
        Overpayments held as credit instead of refunded: from credit notes submitted without a
        refund UTR, and from POs revised down or cancelled after payment. It is set off against
        the facility&apos;s next payments.
      </Typography>

      <Box display="flex" gap={1} flexWrap="wrap" my={2} role="group" aria-label="Filter open advance by state">
        {filters.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            clickable
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
            color={filter === f.key ? "primary" : "default"}
            variant={filter === f.key ? "filled" : "outlined"}
          />
        ))}
      </Box>

      {historyLoading && visible.length === 0 ? (
        <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", p: 3, textAlign: "center" }}>
          <Typography fontSize="14px" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : visible.length === 0 ? (
        <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", p: 3, textAlign: "center" }}>
          <Typography fontSize="14px" color={history.isError ? "error" : "text.secondary"}>
            {history.isError ? "Couldn't load these entries. Please try again." : "No entries in this view."}
          </Typography>
        </Box>
      ) : isMobile ? (
        <Box>
          {visible.map((entry) => {
            const lines = entry.adjustments ?? [];
            return (
              <MobileCard key={entry.id} flat>
                <Box py={1} display="flex" flexDirection="column" gap={0.75}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography fontSize="13px" fontWeight={600}>
                      {entrySourceLabel(entry)}
                    </Typography>
                    <StateChip state={entry.state} />
                  </Box>
                  {showFacility && (
                    <Typography fontSize="12px" color="text.secondary">
                      {facilityName(entry)}
                    </Typography>
                  )}
                  <FieldRow label="Credit" value={formatAmount(entry.amount)} />
                  <FieldRow label="Balance" value={<BalanceCell entry={entry} />} />
                  <FieldRow label="Expires" value={<ExpiryCell entry={entry} today={today} />} />
                  {lines.length > 0 && <SetOffList lines={lines} />}
                  {canRefund(entry) && (
                    <Box>
                      <RefundButton entry={entry} onRecordRefund={onRecordRefund} />
                    </Box>
                  )}
                </Box>
              </MobileCard>
            );
          })}
        </Box>
      ) : (
        <TableContainer sx={{ bgcolor: "background.paper", borderRadius: "8px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((heading) => (
                  <TableCell
                    key={heading || "actions"}
                    align={heading === "Credit" || heading === "Balance" ? "right" : "left"}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((entry) => {
                const lines = entry.adjustments ?? [];
                const open = expanded.has(entry.id);
                return (
                  <Fragment key={entry.id}>
                    <TableRow sx={open ? { "& > td": { borderBottom: "none" } } : undefined}>
                      {showFacility && (
                        <TableCell>
                          <Typography fontSize="13px">{facilityName(entry)}</Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography fontSize="13px" fontWeight={600}>
                          {entrySourceLabel(entry)}
                        </Typography>
                        <Typography fontSize="12px" color="text.secondary">
                          Held since {formatIsoDate(entry.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontSize="13px">{formatAmount(entry.amount)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <BalanceCell entry={entry} />
                      </TableCell>
                      <TableCell>
                        <ExpiryCell entry={entry} today={today} />
                      </TableCell>
                      <TableCell>
                        <StateChip state={entry.state} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                          {canRefund(entry) && <RefundButton entry={entry} onRecordRefund={onRecordRefund} />}
                          {lines.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => toggle(entry.id)}
                              aria-expanded={open}
                              aria-label={`${open ? "Hide" : "Show"} set-offs for ${entrySourceLabel(entry)}`}
                            >
                              {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow>
                        <TableCell colSpan={columns.length} sx={{ bgcolor: V2_COLORS.bgMuted, py: 1.5 }}>
                          <Box maxWidth={520} ml="auto">
                            <SetOffList lines={lines} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {historyStates.length > 0 && history.isError && visible.length > 0 && (
        <Typography fontSize="13px" color="error" mt={1.5}>
          Couldn&apos;t load more entries. Please try again.
        </Typography>
      )}
      {historyStates.length > 0 && history.hasNextPage && (
        <Box textAlign="center" mt={1.5}>
          <Button
            size="small"
            variant="outlined"
            disabled={history.isFetchingNextPage}
            onClick={() => history.fetchNextPage()}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {history.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
